/**
 * @typedef {import ('../interfaces/AUI/IUploadFile.interface').IUploadFile} IUploadFile
 * @typedef {import ('../interfaces/AUI/IUploadFile.interface').IUploadFiles} IUploadFiles
 * @typedef {import ('../interfaces/AUI/IUploadFile.interface').IUploadErrors} IUploadErrors
 * @typedef {import ('../interfaces/AUI/IUploadFile.interface').IUploadValidate} IUploadValidate
 */

import { useState } from 'react';

/**
 * Listado de modos
 *
 * @readonly
 * @enum {string}
 */
const MODE = {
  ONSUBMIT: 'onSubmit',
  ONCHANGE: 'onChange',
};

/**
 * Hook para simplificar la gestión de
 * los campos de carga de archivos
 * AUIInputUploadBasic, aplicando un register
 * para minimizar los datos a gestionar en
 * los formularios.
 *
 * @param {Object} param
 * @param {'onSubmit' | 'onChange'} param.mode
 * @returns
 */
const useUploadFile = ({ mode = 'onChange' }) => {
  /**
   * @type {[IUploadFiles, React.Dispatch<IUploadFiles>]} state
   */
  const [files, setFiles] = useState({});
  /**
   * @type {[IUploadErrors, React.Dispatch<IUploadErrors>]} state
   */
  const [uploadErrors, setUploadErrors] = useState({});
  /**
   * @type {[Object.<string, string>, React.Dispatch<Object.<string, string>>]} state
   */
  const [preloadFileNames, setPreloadFileNames] = useState({});
  /**
   * @type {[Object.<string, boolean>, React.Dispatch<Object.<string, boolean>>]} state
   */
  const [resetForm, setResetForm] = useState({});

  /**
   * Actualiza el estado de los errores,
   * solamente aplica validación aquellos
   * campos que tiene el flag required a true.
   *
   * @param {string} name
   * @param {IUploadFiles} newState
   */
  const validateFile = (name, newState) => {
    setUploadErrors({
      ...uploadErrors,
      [name]:
        newState[name].required && (!newState[name].file || !newState[name].preloadFileName)
          ? newState[name].error
          : '',
    });
  };

  /**
   * Gestiona el eliminar el archivo
   * seleccionado.
   *
   * @param {string} name
   * @param {boolean} [hasResetField=false]
   */
  const removeFile = (name, hasResetField = false) => {
    let newState = { ...files, [name]: { ...files[name], preloadFileName: null, file: null } };

    /**
     * Solamente se actualiza el estado validado
     * en validateFile en caso que el Hook este
     * configurado con el modo: onChange.
     */
    if (mode === MODE.ONCHANGE) {
      validateFile(name, newState);
    }

    if (!files[name].file) {
      setPreloadFileNames({ ...preloadFileNames, [name]: null });
      newState = { ...newState, [name]: { ...newState[name], isDirty: !newState[name].preloadFileName } };
    } else {
      newState = { ...newState, [name]: { ...newState[name], isDirty: false } };
    }

    setFiles(newState);
    if (hasResetField) {
      setResetForm({ ...resetForm, [name]: !!files[name] });
    }
  };

  /**
   * Gestiona el evento al eliminar el archivo
   * seleccionado.
   *
   * @param {string} name
   */
  const onRemoveFile = (name) => removeFile(name);

  /**
   * Gestiona el evento al agregar el archivo
   * seleccionado en el prompt del navegador.
   *
   * @param {string} name
   * @param {object} file
   */
  const onSelectFile = (name, file) => {
    setUploadErrors({ ...uploadErrors, [name]: '' });
    setFiles({ ...files, [name]: { ...files[name], file, isDirty: true } });
    setResetForm({ ...resetForm, [name]: false });
  };

  /**
   * Gestiona el evento cuando se producen
   * errores en al selección de una archivo
   * ya sea porque la extensión no es valida
   * o porque se supera el tamaño permitido
   * del archivo informado en la propiedad
   * del registro accept.
   *
   * @param {string} name
   * @param {object} error
   */
  const onErrorFile = (name, { error }) => {
    setUploadErrors({ ...uploadErrors, [name]: error });
  };

  /**
   * Registra un componente AUIInputUploadBasic
   * al Hook. Este devuelve las principales props
   * que debe de gestionar internamiento.
   *
   * @param {IUploadFile} param
   * @returns
   */
  const register = ({ name, ...rest }) => {
    if (name in files !== true) {
      setFiles({ ...files, [name]: { name, file: null, ...rest, preloadFileName: null, isDirty: false } });
      setPreloadFileNames({ ...preloadFileNames, [name]: rest.preloadFileName });
      setResetForm({ ...resetForm, [name]: false });
    }

    const { accept, required } = rest;
    return {
      error: uploadErrors[name],
      accept,
      required,
      resetForm: resetForm[name],
      preloadFileName: preloadFileNames[name],
      onRemoveFile: () => onRemoveFile(name),
      onSelectFile: (file) => onSelectFile(name, file),
      onErrorFile: (error) => onErrorFile(name, error),
    };
  };

  /**
   * Asigna los preloadFileName al estado.
   *
   * @param {Object.<string, string>} preloadFileName
   * @param {boolean} [clearDirty=false]
   */
  const addPreloadFileNames = (preloadFileName, clearDirty = false) => {
    let newStateFiles = files;
    let newStatePreloadFileNames = preloadFileNames;

    Object.keys(preloadFileName).forEach((file) => {
      newStateFiles = {
        ...newStateFiles,
        [file]: { ...newStateFiles[file], file: null, preloadFileName: preloadFileName[file], isDirty: clearDirty },
      };
      newStatePreloadFileNames = { ...newStatePreloadFileNames, [file]: preloadFileName[file] };
    });

    setFiles(newStateFiles);
    setPreloadFileNames(newStatePreloadFileNames);
  };

  /**
   * Informa si alguno de los campos controlados
   * por el Hook se ha modificado.
   *
   * @returns {boolean}
   */
  const isDirtyUploads = () => Object.keys(files).some((item) => files[item].isDirty);

  /**
   * Informa si los campos han sido validados
   * por el Hook.
   *
   * @returns {boolean}
   */
  const isValidateUploads = () =>
    !Object.keys(files).some((file) => files[file].required && !files[file].file && !preloadFileNames[file]);

  /**
   * Validación y recopilación de los errores para que se
   * gestione en los formularios igual que los
   * errores reportador por ejemplo por React Hook Forms.
   *
   * @returns {IUploadValidate}
   */
  const validate = () => {
    /**
     * @type {IUploadValidate['listErrors']}
     */
    const listErrors = {};
    /**
     * @type {IUploadValidate['listFiles']}
     */
    const listFiles = {};
    Object.keys(files).forEach((file) => {
      if (files[file].required && !files[file].file && !preloadFileNames[file]) {
        listErrors[file] = files[file].error;
      } else {
        listFiles[file] = files[file].file || preloadFileNames[file];
      }
    });

    setUploadErrors(listErrors);

    return { listErrors, listFiles };
  };

  return { files, register, validate, addPreloadFileNames, isDirtyUploads, isValidateUploads, removeFile };
};

export default useUploadFile;
